import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import TopPromoBar from '../components/TopPromoBar';
import Header from '../components/Header';
import AuthModal from '../components/AuthModal';
import logo from '../logo.png';
import config from '../config';
import { formatCurrency } from '../utils/currency';
import { createOrder } from '../services/api';

function Checkout({ user, cart, onCartUpdate, onLogout, onLoginSuccess }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { showError } = useToast();
  const { app } = config;
  const APP_NAME = app.name;
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  
  // Get selected items from location state or use all cart items
  const selectedItems = useMemo(() => location.state?.selectedItems || cart || [], [location.state?.selectedItems, cart]);
  
  const [loading, setLoading] = useState(false);
  const [orderNotes, setOrderNotes] = useState('');

  useEffect(() => {
    // Redirect if no items
    if (!selectedItems || selectedItems.length === 0) {
      navigate('/');
      return;
    }
    // If not authenticated, show error and open auth modal (only once)
    if (!user && !isAuthModalOpen) {
      showError('Please log in to continue with checkout.');
      setIsAuthModalOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, selectedItems, navigate]);

  const calculateTotal = () => {
    return selectedItems.reduce((sum, item) => {
      const price = parseFloat(item.price) || 0;
      const quantity = item.quantity || 1;
      return sum + (price * quantity);
    }, 0);
  };


  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!user) {
      showError('Please log in to continue with checkout.');
      setIsAuthModalOpen(true);
      return;
    }
    setLoading(true);
    try {
      // API requires shipping_address and billing_address; we don't collect them at checkout
      const placeholderAddress = '—';
      const orderData = {
        items: selectedItems.map(item => ({
          product_id: item.id,
          quantity: item.quantity,
        })),
        shipping_address: placeholderAddress,
        billing_address: placeholderAddress,
        phone: user?.phone || null,
        notes: orderNotes?.trim() || null,
      };
      
      // Create order first (with pending_payment status)
      const token = localStorage.getItem('auth_token') || localStorage.getItem('token');
      if (!token) {
        showError('Authentication required. Please log in again.');
        setIsAuthModalOpen(true);
        return;
      }
      
      const createdOrder = await createOrder(orderData, token);
      
      if (!createdOrder || !createdOrder.id) {
        showError('Failed to create order. Please try again.');
        return;
      }
      
      console.log('Order created:', createdOrder.id, createdOrder.invoice_number);
      
      // Navigate to payment page with order_id
      navigate('/payment', {
        state: {
          orderId: createdOrder.id,
          order: createdOrder,
          selectedItems,
        }
      });
    } catch (error) {
      console.error('Failed to create order:', error);
      showError(error.message || 'Failed to create order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const total = calculateTotal();
  const cartButtonRef = React.useRef(null);

  return (
    <div className="App">
      <TopPromoBar appName={APP_NAME} />
      <Header 
        logo={logo} 
        appName={APP_NAME}
        cartCount={cart?.length || 0}
        onCartClick={() => navigate('/')}
        cartButtonRef={cartButtonRef}
        searchQuery=""
        onSearchChange={() => {}}
        activeFilter={null}
        onFilterChange={() => {}}
        onCategoryFilterChange={() => {}}
        user={user}
        onLoginSuccess={onLoginSuccess || (() => {})}
        onLogout={onLogout}
      />
      
      {/* Auth Modal for checkout login */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        onLoginSuccess={(user, token, options) => {
          if (onLoginSuccess) {
            onLoginSuccess(user, token, options);
          }
          setIsAuthModalOpen(false);
        }}
        redirectAfterLogin={true}
        redirectAfterRegister={true}
      />
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 pt-[100px] sm:pt-[120px] pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-6 sm:mb-8">
            <div className="flex items-center gap-2 sm:gap-3 mb-2">
              <div className="w-1 h-8 sm:w-1.5 sm:h-10 bg-gradient-to-b from-primary via-primary to-primary-dark rounded-full shadow-lg shadow-primary/30"></div>
              <div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 tracking-tight mb-1 sm:mb-2">Checkout</h1>
                <p className="text-sm sm:text-base text-gray-600 font-medium">Complete your order</p>
              </div>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="max-w-lg mx-auto space-y-4">
              {/* Optional: link to update profile */}
              <div className="flex justify-center">
                <button
                  type="button"
                  onClick={() => navigate('/profile')}
                  className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-primary font-medium"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                  </svg>
                  Update Profile Information (optional)
                </button>
              </div>

              {/* Order Summary */}
              <div className="bg-white rounded-xl sm:rounded-2xl border-2 border-gray-200/50 p-4 sm:p-6 shadow-lg">
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">Order Summary</h2>
                  
                  {/* Order Items */}
                  <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6 max-h-64 sm:max-h-96 overflow-y-auto">
                    {selectedItems.map((item) => {
                      const itemPrice = parseFloat(item.price) || 0;
                      const quantity = item.quantity || 1;
                      const itemTotal = itemPrice * quantity;
                      
                      return (
                        <div key={item.id} className="flex gap-2 sm:gap-3 pb-3 sm:pb-4 border-b border-gray-200 last:border-0">
                          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-lg overflow-hidden bg-gray-100 flex-shrink-0">
                            <img 
                              src={item.image || 'https://via.placeholder.com/100'} 
                              alt={item.name}
                              className="w-full h-full object-cover"
                            />
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-gray-900 text-xs sm:text-sm truncate">{item.name}</h3>
                            {(item.selectedColor || item.selectedSize) && (
                              <div className="flex flex-wrap gap-1.5 sm:gap-2 mt-1">
                                {item.selectedColor && (
                                  <span className="text-[10px] sm:text-xs text-gray-600">Color: {item.selectedColor}</span>
                                )}
                                {item.selectedSize && (
                                  <span className="text-[10px] sm:text-xs text-gray-600">Size: {item.selectedSize}</span>
                                )}
                              </div>
                            )}
                            <div className="flex items-center justify-between mt-1.5 sm:mt-2">
                              <span className="text-[10px] sm:text-xs text-gray-600">Qty: {quantity}</span>
                              <span className="font-bold text-primary text-sm sm:text-base">{formatCurrency(itemTotal)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Optional order notes */}
                  <div className="mb-4">
                    <label className="block text-xs sm:text-sm font-semibold text-gray-700 mb-1.5">Order notes (optional)</label>
                    <textarea
                      value={orderNotes}
                      onChange={(e) => setOrderNotes(e.target.value)}
                      className="w-full px-3 py-2 text-sm border-2 border-gray-200 rounded-lg focus:border-primary focus:ring-2 focus:ring-primary/20 resize-none bg-white"
                      rows={2}
                      placeholder="Special instructions for this order"
                    />
                  </div>
                  
                  {/* Total */}
                  <div className="border-t-2 border-gray-200 pt-3 sm:pt-4 space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-base sm:text-lg font-semibold text-gray-700">Total</span>
                      <span className="text-xl sm:text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
                        {formatCurrency(total)}
                      </span>
                    </div>
                    
                    <button
                      type="submit"
                      disabled={loading || selectedItems.length === 0}
                      className="w-full py-3 sm:py-4 bg-gradient-to-r from-primary via-primary-dark to-secondary text-white rounded-lg sm:rounded-xl font-bold text-base sm:text-lg hover:shadow-2xl hover:shadow-primary/30 hover:-translate-y-1 transition-all duration-300 transform disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:translate-y-0"
                    >
                      {loading ? (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="animate-spin h-4 w-4 sm:h-5 sm:w-5" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          <span className="text-sm sm:text-base">Buying...</span>
                        </span>
                      ) : (
                        <span className="flex items-center justify-center gap-2">
                          <svg className="w-4 h-4 sm:w-5 sm:h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          Buy Now
                        </span>
                      )}
                    </button>
                    
                    <button
                      type="button"
                      onClick={() => navigate('/')}
                      className="w-full py-2.5 sm:py-3 text-sm sm:text-base text-gray-600 hover:text-gray-900 font-semibold rounded-lg sm:rounded-xl hover:bg-gray-50 transition-all"
                    >
                      Continue Shopping
                    </button>
                  </div>
                </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default Checkout;

